import React, { useEffect, useRef } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import { formateaMonto, formateaRut, LIMITES, MAX_DIGITOS_MONTO, montoSinFormato } from '../../common/ingreso-manual';

// El error se muestra al intentar revisar (submitCount) o al salir del campo, no mientras se escribe en otro.
const hayError = (formik, name) => !!formik.errors[name] && (!!formik.touched[name] || formik.submitCount > 0);

const Etiqueta = ({ label, requerido }) => (
    <Form.Label className="mb-1">
        <b>{label}</b>
        {requerido ? <span className="text-danger"> *</span> : null}
    </Form.Label>
);

// rows: si viene, el campo es un área de texto de esa altura (p. ej. el relato).
// maxLen: tope de caracteres propio del campo; si no viene, se usa el de LIMITES (columna de la BD).
// onSalida: se ejecuta además del blur normal, al salir del campo (p. ej. para verificar si el siniestro ya existe).
// onCambio(valor): se ejecuta además del cambio normal, con el valor nuevo (p. ej. para completar otro campo).
export const Texto = ({ formik, name, label, requerido, md = 3, type = 'text', placeholder, inputMode, max, readOnly, rows, maxLen, onSalida, onCambio }) => (
    <Form.Group as={Col} md={md} controlId={name} className="mb-3">
        <Etiqueta label={label} requerido={requerido} />
        <Form.Control
            as={rows ? 'textarea' : undefined}
            rows={rows}
            type={rows ? undefined : type}
            name={name}
            placeholder={placeholder}
            inputMode={inputMode}
            max={max}
            maxLength={maxLen || LIMITES[name]}
            readOnly={readOnly}
            value={formik.values[name]}
            onChange={(e) => {
                formik.handleChange(e);
                if (onCambio) onCambio(e.target.value);
            }}
            onBlur={(e) => {
                formik.handleBlur(e);
                if (onSalida) onSalida(e);
            }}
            isInvalid={hayError(formik, name)}
        />
        <Form.Control.Feedback type="invalid">{formik.errors[name]}</Form.Control.Feedback>
        {rows && LIMITES[name] ? (
            <Form.Text className="text-muted d-block text-end">{String(formik.values[name] || '').length} / {LIMITES[name]}</Form.Text>
        ) : null}
    </Form.Group>
);

// Monto en pesos con separador de miles (1.500.000). En el formulario se guardan solo los dígitos ("1500000"), así que la
// validación no cambia; el formato es solo de pantalla y al servidor va sin formato (ver construyePayload).
export const Monto = ({ formik, name, label, md = 3, placeholder = '0', requerido }) => (
    <Form.Group as={Col} md={md} controlId={name} className="mb-3">
        <Etiqueta label={label} requerido={requerido} />
        <Form.Control
            type="text"
            inputMode="numeric"
            name={name}
            placeholder={placeholder}
            value={formateaMonto(formik.values[name])}
            onChange={(e) => formik.setFieldValue(name, montoSinFormato(e.target.value).slice(0, MAX_DIGITOS_MONTO))}
            onBlur={formik.handleBlur}
            isInvalid={hayError(formik, name)}
        />
        <Form.Control.Feedback type="invalid">{formik.errors[name]}</Form.Control.Feedback>
    </Form.Group>
);

// Rut con formato 12.345.678-9 mientras se escribe y al salir del campo. El valor del formulario queda con formato (lo acepta
// rutValido); al servidor va sin puntos (12345678-9, ver construyePayload).
export const Rut = ({ formik, name, label, md = 3 }) => (
    <Form.Group as={Col} md={md} controlId={name} className="mb-3">
        <Etiqueta label={label} />
        <Form.Control
            type="text"
            name={name}
            placeholder="Ej. 12.345.678-9"
            value={formik.values[name]}
            onChange={(e) => formik.setFieldValue(name, formateaRut(e.target.value))}
            onBlur={(e) => {
                formik.setFieldValue(name, formateaRut(e.target.value));
                formik.handleBlur(e);
            }}
            isInvalid={hayError(formik, name)}
        />
        <Form.Control.Feedback type="invalid">{formik.errors[name]}</Form.Control.Feedback>
    </Form.Group>
);

// opciones: [{ id, nombre }]
// onCambio(valor): se ejecuta además del cambio normal (p. ej. para completar otro campo). deshabilitado: solo lectura.
export const Combo = ({ formik, name, label, requerido, md = 3, opciones, ayuda, onCambio, deshabilitado }) => (
    <Form.Group as={Col} md={md} controlId={name} className="mb-3">
        <Etiqueta label={label} requerido={requerido} />
        <Form.Select
            name={name}
            value={formik.values[name]}
            disabled={deshabilitado}
            onChange={(e) => {
                formik.handleChange(e);
                if (onCambio) onCambio(e.target.value);
            }}
            onBlur={formik.handleBlur}
            isInvalid={hayError(formik, name)}
        >
            <option value="">Seleccione...</option>
            {/* La clave incluye la posición: algunos catálogos repiten el id (p. ej. ciudades: MAG = MAGALLANES y MARGA MARGA). */}
            {(opciones || []).map((o, i) => (
                <option key={`${o.id}-${i}`} value={o.id}>{o.nombre}</option>
            ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">{formik.errors[name]}</Form.Control.Feedback>
        {ayuda ? <Form.Text className="text-muted">{ayuda}</Form.Text> : null}
    </Form.Group>
);

// Archivo opcional (la carta de cobro). El valor del formulario es el File (o null). Al limpiar el formulario se vacía también el
// selector, que no se controla desde React.
export const Archivo = ({ formik, name, label, md = 12, accept, ayuda }) => {
    const entrada = useRef(null);
    const archivo = formik.values[name];
    useEffect(() => {
        if (!archivo && entrada.current) entrada.current.value = '';
    }, [archivo]);
    return (
        <Form.Group as={Col} md={md} controlId={name} className="mb-3">
            <Etiqueta label={label} />
            <Form.Control
                ref={entrada}
                type="file"
                name={name}
                accept={accept}
                onChange={(e) => formik.setFieldValue(name, e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                onBlur={formik.handleBlur}
                isInvalid={hayError(formik, name)}
            />
            <Form.Control.Feedback type="invalid">{formik.errors[name]}</Form.Control.Feedback>
            {ayuda ? <Form.Text className="text-muted">{ayuda}</Form.Text> : null}
        </Form.Group>
    );
};

// Dato calculado, solo lectura (p. ej. la ciudad, que sale de la comuna).
export const TextoLectura = ({ label, valor, md = 3, ayuda }) => (
    <Form.Group as={Col} md={md} className="mb-3">
        <Etiqueta label={label} />
        <Form.Control type="text" value={valor || ''} readOnly />
        {ayuda ? <Form.Text className="text-muted">{ayuda}</Form.Text> : null}
    </Form.Group>
);

export const Seccion = ({ titulo, opcional, children }) => (
    <Card className="mb-3">
        <Card.Header as="h6" className="text-white" style={{ backgroundColor: '#4F517A' }}>
            {titulo}
            {opcional ? <small className="ms-2 fw-normal">(opcional)</small> : null}
        </Card.Header>
        <Card.Body>
            <Row>{children}</Row>
        </Card.Body>
    </Card>
);
